/**
 * product-zoom-lens.js
 * Lens-style zoom for product images.
 *
 * KEY FIX: Shopify images use object-fit:contain inside their container,
 * so the actual rendered image is smaller than the <img> element's DOM rect.
 * We calculate the true rendered image bounds to get pixel-perfect alignment.
 */

(function () {
  'use strict';

  const LENS_SIZE = 258; // px — smaller = more zoom

  let lens        = null;
  let resultPanel = null;
  let fogOverlay  = null;
  let currentImg  = null;
  let zoomRect    = null;

  // ── Create DOM elements ──────────────────────────────────────────────────────
  function createElements() {
    lens = document.createElement('div');
    lens.className = 'zoom-lens';
    document.body.appendChild(lens);

    resultPanel = document.createElement('div');
    resultPanel.className = 'zoom-result';
    document.body.appendChild(resultPanel);

    fogOverlay = document.createElement('div');
    fogOverlay.className = 'zoom-fog-overlay';
    document.body.appendChild(fogOverlay);
  }

  // ── Get the ACTUAL rendered image rect inside an object-fit:contain box ──────
  // The <img> DOM rect equals its container, but the real image is letterboxed.
  function getActualImgRect(img) {
    const domRect = img.getBoundingClientRect();
    const nW = img.naturalWidth;
    const nH = img.naturalHeight;

    if (!nW || !nH) return domRect; // fallback if natural size not loaded

    const containerW = domRect.width;
    const containerH = domRect.height;
    const containerRatio = containerW / containerH;
    const imgRatio       = nW / nH;

    let renderedW, renderedH, offsetX, offsetY;

    if (imgRatio > containerRatio) {
      // Image wider → constrained by container width, gray bars top/bottom
      renderedW = containerW;
      renderedH = containerW / imgRatio;
      offsetX   = 0;
      offsetY   = (containerH - renderedH) / 2;
    } else {
      // Image taller → constrained by container height, gray bars left/right
      renderedH = containerH;
      renderedW = containerH * imgRatio;
      offsetX   = (containerW - renderedW) / 2;
      offsetY   = 0;
    }

    return {
      left:   domRect.left + offsetX,
      top:    domRect.top  + offsetY,
      right:  domRect.left + offsetX + renderedW,
      bottom: domRect.top  + offsetY + renderedH,
      width:  renderedW,
      height: renderedH,
    };
  }

  // ── Get highest resolution src ───────────────────────────────────────────────
  function getHiResSrc(img) {
    const candidates = [];
    if (img.srcset) {
      img.srcset.split(',').forEach(part => {
        const [url, w] = part.trim().split(/\s+/);
        candidates.push({ url: url || '', w: parseInt(w) || 0 });
      });
    }
    let best = img.src;
    if (candidates.length) {
      candidates.sort((a, b) => b.w - a.w);
      best = candidates[0].url || best;
    }
    // Strip Shopify size suffix (_NNNxNNN) to get original full-res
    best = best.replace(/(_\d+x\d*|_\d*x\d+)(\.(?:jpg|jpeg|png|webp|gif))/i, '$2');
    return best;
  }

  // ── Find the zoom target position (viewport clamped so it is never cropped) ──
  function getZoomPanelPosition(imgRect) {
    const infoEl = document.querySelector('.product__info-wrapper') ||
                   document.querySelector('[id^="ProductInfo-"]');
    
    let targetLeft;
    let targetWidth;

    if (infoEl) {
      const rect = infoEl.getBoundingClientRect();
      targetLeft = rect.left + 28; // Shift slightly to the right side
      targetWidth = rect.width - 28;
    } else {
      targetLeft = imgRect.right + 36;
      targetWidth = 520;
    }

    // Panel size square, clamped to available viewport height and column width
    const availableHeight = window.innerHeight - 40;
    const panelSize = Math.max(360, Math.min(520, targetWidth, availableHeight));

    // Clamp panel left so it never overflows the right screen edge
    const maxLeft = Math.max(20, window.innerWidth - panelSize - 20);
    targetLeft = Math.min(targetLeft, maxLeft);

    // Clamp panel top so it is ALWAYS fully visible in the current viewport
    const minTop = 20;
    const maxTop = Math.max(minTop, window.innerHeight - panelSize - 20);

    // Ideal top aligns with the top of the currently hovered image
    const targetTop = Math.max(minTop, Math.min(maxTop, imgRect.top));

    return {
      left: targetLeft,
      top: targetTop,
      size: panelSize
    };
  }

  // ── Mouse move handler ───────────────────────────────────────────────────────
  function onMouseMove(e) {
    if (!currentImg) return;

    // Use the ACTUAL rendered image rect (accounts for object-fit:contain letterboxing)
    const imgRect = getActualImgRect(currentImg);
    if (!imgRect || imgRect.width <= 0 || imgRect.height <= 0) return;

    // Mouse position relative to the actual image (not the DOM element)
    const mouseX = e.clientX - imgRect.left;
    const mouseY = e.clientY - imgRect.top;

    // If cursor is outside the actual image area, hide
    if (mouseX < 0 || mouseY < 0 || mouseX > imgRect.width || mouseY > imgRect.height) {
      lens.style.display        = 'none';
      resultPanel.style.display = 'none';
      return;
    }

    lens.style.display        = 'block';
    resultPanel.style.display = 'block';

    // Lens size: default 258px, but cannot exceed image dimensions
    const maxLensSize = Math.min(LENS_SIZE, imgRect.width, imgRect.height);
    const actualLensSize = Math.max(60, maxLensSize);

    // Lens top-left, clamped within image bounds
    let lensX = mouseX - actualLensSize / 2;
    let lensY = mouseY - actualLensSize / 2;
    lensX = Math.max(0, Math.min(imgRect.width  - actualLensSize, lensX));
    lensY = Math.max(0, Math.min(imgRect.height - actualLensSize, lensY));

    // Position lens square on screen
    lens.style.left   = (imgRect.left + lensX) + 'px';
    lens.style.top    = (imgRect.top  + lensY) + 'px';
    lens.style.width  = actualLensSize + 'px';
    lens.style.height = actualLensSize + 'px';

    const panelPos = getZoomPanelPosition(imgRect);
    const panelSize = panelPos.size;
    const scale     = panelSize / actualLensSize; // pixels per source pixel

    // Background = full image scaled up, offset so lens area is at origin
    const bgW = imgRect.width  * scale;
    const bgH = imgRect.height * scale;
    const bgX = -(lensX * scale);
    const bgY = -(lensY * scale);

    resultPanel.style.left   = panelPos.left + 'px';
    resultPanel.style.top    = panelPos.top  + 'px';
    resultPanel.style.width  = panelSize + 'px';
    resultPanel.style.height = panelSize + 'px';

    resultPanel.style.backgroundSize     = `${bgW}px ${bgH}px`;
    resultPanel.style.backgroundPosition = `${bgX}px ${bgY}px`;
  }

  // ── Mouse enter ──────────────────────────────────────────────────────────────
  function onMouseEnter(e) {
    if (window.innerWidth < 990) return;

    const img = e.currentTarget.querySelector('img');
    if (!img) return;

    currentImg = img;
    
    const infoEl = document.querySelector('.product__info-wrapper') || document.querySelector('[id^="ProductInfo-"]');
    if (infoEl) {
      const rect = infoEl.getBoundingClientRect();
      const visibleTop = Math.max(0, rect.top);
      const visibleBottom = Math.min(window.innerHeight, rect.bottom);
      if (visibleBottom > visibleTop) {
        fogOverlay.style.left   = rect.left + 'px';
        fogOverlay.style.top    = visibleTop + 'px';
        fogOverlay.style.width  = rect.width + 'px';
        fogOverlay.style.height = (visibleBottom - visibleTop) + 'px';
        fogOverlay.classList.add('is-visible');
      }
    }

    const hiRes = getHiResSrc(img);
    resultPanel.style.backgroundImage  = `url('${hiRes}')`;
    resultPanel.style.backgroundRepeat = 'no-repeat';

    lens.style.display        = 'block';
    resultPanel.style.display = 'block';

    onMouseMove(e);
  }

  // ── Mouse leave ──────────────────────────────────────────────────────────────
  function onMouseLeave() {
    lens.style.display        = 'none';
    resultPanel.style.display = 'none';
    fogOverlay.classList.remove('is-visible');
    
    currentImg = null;
    zoomRect   = null;
  }

  // ── Attach to product image containers ──────────────────────────────────────
  function attachToImages() {
    document.querySelectorAll(
      '.product__media-list .product__media-item .product-media-container'
    ).forEach(container => {
      if (container.dataset.zoomAttached) return;
      container.dataset.zoomAttached = '1';
      container.style.cursor = 'crosshair';
      container.addEventListener('mouseenter', onMouseEnter);
      container.addEventListener('mousemove',  onMouseMove);
      container.addEventListener('mouseleave', onMouseLeave);
    });
  }

  // ── Init ─────────────────────────────────────────────────────────────────────
  function init() {
    if (window.innerWidth < 990) return;
    createElements();
    attachToImages();

    document.addEventListener('variant:changed', () => setTimeout(attachToImages, 400));

    const gallery = document.querySelector('.product__media-list');
    if (gallery) {
      new MutationObserver(() => setTimeout(attachToImages, 100))
        .observe(gallery, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
